import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Stack from '@mui/material/Stack'

//components
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import { useDateSelectorContext } from '../../context/DateSelectorContext'

//services
import liveDataService from '../../service/liveData.service'
import { useDashboardGlobalStateContext } from '../../context/DashboardGlobalStateContext'

const BoxContainer = styled('div')(() => ({
  width: '100%',
  height: '70vh',
  overflow: 'auto',
  border: '1px solid #ccc',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}))

let defaultMachine = {
  value: 0,
  label: ''
}

const EnergyRecord = () => {
  const [energyRecordRows, setEnergyRecordRows] = useState([])
  const [machine, setMachine] = useState(defaultMachine[0])
  const { selectedDate } = useDateSelectorContext()
  const [availableShifts, setAvailableShifts] = useState([])
  const [selectedShift, setSelectedShift] = useState({
    label: 'All shifts',
    value: 0
  })
  const [isLoading, setIsLoading] = useState(false)

  const { machines: machineOptions } = useDashboardGlobalStateContext()

  const fetchEnergyRecords = async () => {
    const params = {}

    if (selectedDate) {
      params.date = selectedDate
    }

    try {
      setIsLoading(true)
      const energyRecordResponse =
        await liveDataService.getEnergyRecords(params)
      console.log(energyRecordResponse)
      const data = energyRecordResponse.data.data
      const availableShifts = []
      const partsData = data.map((data, i) => {
        const shiftValue = `S${data.shift}`
        if (!availableShifts.includes(shiftValue)) {
          availableShifts.push(shiftValue)
        }
        return {
          index: i + 1,
          machine: data.machine,
          time: data.time,
          shift: shiftValue,
          tag1: data.tag1,
          tag2: data.tag2,
          tag3: data.tag3,
          tag4: data.tag4,
          tag5: data.tag5,
          tag6: data.tag6
        }
      })

      setAvailableShifts([
        {
          label: 'All shifts',
          value: 0
        },
        ...availableShifts.map((shift) => {
          return { label: shift, value: shift }
        })
      ])
      setEnergyRecordRows(partsData)
    } catch (err) {
      console.log(err)
      setEnergyRecordRows([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (machine === null) {
      return
    }
    fetchEnergyRecords()
  }, [selectedDate])

  useEffect(() => {
    // Set default machine to the first machine in the options list
    if (machineOptions.length > 0) {
      setMachine(machineOptions[0])
    }
  }, [])

  //1. Create search object
  const searchObj = {
    ...(machine &&
      (machine.value !== 'all' || machine.value === 0) && {
        machine: machine.value
      }),
    ...(selectedShift &&
      selectedShift.value &&
      selectedShift.value !== 0 && {
        shift: selectedShift.value
      })
  }

  //2. Extract the keys into an array
  const keysToChecked = Object.keys(searchObj)
  console.log('keysToChecked', keysToChecked, searchObj)

  //3. filter
  let filteredRows = energyRecordRows
  if (keysToChecked.length > 0) {
    filteredRows = energyRecordRows.filter((energyRecordRow) => {
      return keysToChecked.every(
        (key) => searchObj[key] === energyRecordRow[key]
      )
    })
  }
  const formatToTwoDecimalPlaces = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2)
    }
    return value
  }

  const energyRecordColumns = [
    {
      Header: 'S.No',
      accessor: 'index'
    },
    {
      Header: 'Machine',
      accessor: 'machine'
    },
    {
      Header: 'Time',
      accessor: 'time'
    },
    {
      Header: 'Shift',
      accessor: 'shift'
    },
    {
      Header: 'Voltge [V]',
      accessor: 'tag1',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Current [A]',
      accessor: 'tag2',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Power [Kw]',
      accessor: 'tag3',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },

    {
      Header: 'Frequency [Hz]',
      accessor: 'tag4',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Energy [KWh]',
      accessor: 'tag5',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Power Factor',
      accessor: 'tag6',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    }
  ]

  const TotalEnergyDisplay = ({ filteredRows }) => {
    // Get the first and last energy values from filtered rows
    const firstEnergy =
      filteredRows.length > 0
        ? parseFloat(filteredRows[filteredRows.length - 1].tag5)
        : 0
    const lastEnergy =
      filteredRows.length > 0 ? parseFloat(filteredRows[0].tag5) : 0

    // Calculate the difference
    const energyDifference = (lastEnergy - firstEnergy).toFixed(3)

    return (
      <div style={{ marginTop: '2px' }}>
        <strong>Energy Difference:</strong> {energyDifference} KWh
      </div>
    )
  }

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee'
        }}
      >
        <StyledSelectField
          label=""
          placeHolderText="Select shift"
          selectedValue={selectedShift}
          selectOptions={availableShifts}
          onChangeHandler={(value) => setSelectedShift(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No shift found"
        />
        <StyledSelectField
          label=""
          placeHolderText=""
          selectedValue={machine}
          selectOptions={machineOptions}
          onChangeHandler={(value) => setMachine(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No machine found"
        />
      </Stack>
      <Stack
        sx={{
          gap: '16px',
          padding: '16px'
        }}
      >
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filteredRows.length === 0 && (
            <StyledEmptyHeader>There are no energy record</StyledEmptyHeader>
          )}
          <TotalEnergyDisplay filteredRows={filteredRows} />
          {!isLoading && filteredRows.length > 0 && (
            <BoxContainer>
              <StyledTable
                pagination={false}
                columns={energyRecordColumns}
                data={filteredRows}
                hiddenColumns={['machine', 'index']}
              />
            </BoxContainer>
          )}
        </StyledTableContainer>
      </Stack>
    </>
  )
}

export default EnergyRecord
